import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import Layout from '../components/Layout';
import useAggiornamentoPeriodico from '../hooks/useAggiornamentoPeriodico';
import useVersioniViste from '../hooks/useVersioniViste';
import ModificataDaAltri from '../components/ModificataDaAltri';
import { messaggioAzione } from '../utils/eliminazione';
import ConflittoVersione, { eConflitto } from '../components/ConflittoVersione';
import PasswordTemporanea from '../components/PasswordTemporanea';
import MostraPasswordCliente from '../components/MostraPasswordCliente';
import ImpostaPasswordCliente from '../components/ImpostaPasswordCliente';
import { useAuth } from '../context/AuthContext';
import { puoAccedere } from '../utils/permessi';
import { etichettaSezione } from '../servizi';
import { validaPassword, REGOLA_PASSWORD_CLIENTE } from '../utils/regolaPassword';

const ETICHETTE_LIVELLO = { '': 'Nessuno', lettura: 'Lettura', scrittura: 'Scrittura' };

export default function Utenti() {
  const { utente: io } = useAuth();
  const puoScrivere = puoAccedere(io, 'utenti', 'scrittura');
  const puoAccount = puoAccedere(io, 'account_clienti', 'scrittura');   // account cliente: creare, reimpostare e mostrare la password
  const [mostraPassword, setMostraPassword] = useState(null);
  const [impostaPassword, setImpostaPassword] = useState(null);
  const [utenti, setUtenti] = useState([]);
  const [sezioni, setSezioni] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [cerca, setCerca] = useState('');
  const [filtroRuolo, setFiltroRuolo] = useState('studio');
  const [mostraDisattivati, setMostraDisattivati] = useState(false);   // spento: gli utenti disattivati restano nel registro ma non compaiono nell'elenco
  const [inModifica, setInModifica] = useState(null); // null | 'nuovo' | utente
  const [credenziali, setCredenziali] = useState(null);
  const [errore, setErrore] = useState('');

  const versioni = useVersioniViste();   // la disattivazione manda la versione che l'operatore ha visto davvero, non quella dell'aggiornamento automatico
  const carica = useCallback(async ({ silenzioso = false } = {}) => {
    try {
      const res = await api.get('/utenti');
      versioni.aggiorna(res.data, { silenzioso });
      setUtenti(res.data);
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Errore nel caricamento degli utenti');
    }
  }, [versioni]);

  useEffect(() => {
    carica();
    api.get('/utenti/sezioni').then(r => { setSezioni(r.data.sezioni.map(x => ({ ...x, nome: etichettaSezione(x) }))); }).catch(() => {});
    if (puoAccedere(io, 'intermittenti')) {
      api.get('/aziende').then(r => setAziende(r.data)).catch(() => {});
    }
  }, [carica, io]);
  useAggiornamentoPeriodico(() => carica({ silenzioso: true }), { attivo: !inModifica && !credenziali && !mostraPassword && !impostaPassword });

  const filtrati = utenti.filter(u => {
    if (filtroRuolo && u.ruolo !== filtroRuolo) return false;
    if (!mostraDisattivati && !u.attivo) return false;
    if (!cerca) return true;
    const q = cerca.toLowerCase();
    return `${u.nome} ${u.cognome} ${u.email} ${u.ragione_sociale || ''}`.toLowerCase().includes(q);
  });

  const cambiaAttivo = async (u) => {
    const azione = u.attivo ? 'Disattivare' : 'Riattivare';
    if (!window.confirm(`${azione} ${u.nome} ${u.cognome}?`)) return;
    try {
      await api.put(`/utenti/${u.id}/attivo`, { attivo: !u.attivo, version: versioni.versioneVista(u) });
      carica();
    } catch (err) {
      window.alert(messaggioAzione(err));
      if (eConflitto(err)) carica();   // qualcun altro l'ha modificato: si mostra la riga aggiornata
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Generare una nuova password temporanea per ${u.nome} ${u.cognome}? Quella attuale smetterà di funzionare.`)) return;
    try {
      const res = await api.post(`/utenti/${u.id}/reset-password`);
      setCredenziali({ email: res.data.email, password: res.data.password_temporanea });
      carica();
    } catch (err) {
      window.alert(err.response?.data?.errore || 'Reset non riuscito');
    }
  };

  return (
    <Layout piena>
      <div style={styles.topBar}>
        <h2 style={{ margin: 0 }}>Utenti ({filtrati.length})</h2>
        {puoScrivere && <button onClick={() => setInModifica('nuovo')} style={styles.btn}>+ Nuovo utente</button>}
      </div>

      {errore && <div style={styles.errore}>{errore}</div>}

      <div style={styles.filtri}>
        <input type="text" placeholder="Cerca per nome, email o azienda..." value={cerca} onChange={e => setCerca(e.target.value)} style={styles.inputCerca} />
        <select value={filtroRuolo} onChange={e => setFiltroRuolo(e.target.value)} style={styles.selectElenco} aria-label="Tipo di utenti">
          <option value="studio">Utenti studio</option>
          <option value="cliente">Utenti azienda</option>
          <option value="">Tutti</option>
        </select>
        <label style={styles.mostraDisattivati}>
          <input type="checkbox" checked={mostraDisattivati} onChange={e => setMostraDisattivati(e.target.checked)} />
          Mostra disattivati
        </label>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Nome</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Ruolo</th>
              {sezioni.map(s => <th key={s.id} style={styles.th}>{s.nome}</th>)}
              <th style={styles.th}>Stato</th>
              {puoScrivere && <th style={styles.th}>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {filtrati.map(u => (
              <tr key={u.id} style={{ ...styles.tr, opacity: u.attivo ? 1 : 0.5 }}>
                <td style={styles.td}>
                  {u.nome} {u.cognome}
                  {u.admin && <span style={styles.badgeAdmin}>admin</span>}
                  {versioni.cambiata(u) && <ModificataDaAltri onRiconosci={() => versioni.riconosci(u)} />}
                </td>
                <td style={styles.td}>{u.email}</td>
                <td style={styles.td}>{u.ruolo === 'studio' ? 'Studio' : `Utente azienda · ${u.ragione_sociale || '—'}`}</td>
                {sezioni.map(s => (
                  <td key={s.id} style={styles.td}>{livelloMostrato(u, s)}</td>
                ))}
                <td style={styles.td}>
                  {!u.attivo ? 'Disattivato' : u.deve_cambiare_password
                    ? <span style={{ color: '#b45309' }}>Password temporanea</span>
                    : <span style={{ color: '#15803d' }}>Attivo</span>}
                </td>
                {puoScrivere && (
                  <td style={styles.td}>
                    <button onClick={() => setInModifica(u)} style={styles.btnSmall}>Modifica</button>
                    {u.attivo && (u.ruolo !== 'cliente' || puoAccount) && <button onClick={() => resetPassword(u)} style={styles.btnSmall}>Reset password</button>}
                    {u.attivo && u.ruolo === 'cliente' && puoAccount && <button onClick={() => setImpostaPassword(u)} style={styles.btnSmall}>Imposta password</button>}
                    {u.attivo && u.ruolo === 'cliente' && puoAccount && <button onClick={() => setMostraPassword(u)} style={styles.btnSmall}>Mostra password</button>}
                    {u.id !== io.id && (
                      <button onClick={() => cambiaAttivo(u)} style={u.attivo ? styles.btnSmallDanger : styles.btnSmall}>
                        {u.attivo ? 'Disattiva' : 'Riattiva'}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {filtrati.length === 0 && (
              <tr><td colSpan={5 + sezioni.length} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>Nessun utente trovato</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {inModifica && (
        <FormUtente
          utente={inModifica === 'nuovo' ? null : inModifica}
          sezioni={sezioni}
          aziende={aziende}
          sonoAdmin={io.admin}
          puoAccount={puoAccount}
          onChiudi={() => setInModifica(null)}
          onSalvato={(risultato) => {
            setInModifica(null);
            if (risultato?.password_temporanea) {
              setCredenziali({ email: risultato.utente.email, password: risultato.password_temporanea });
            }
            carica();
          }}
        />
      )}
      {mostraPassword && (
        <MostraPasswordCliente utenteId={mostraPassword.id} etichetta={`${mostraPassword.nome} ${mostraPassword.cognome} · ${mostraPassword.email}`} onChiudi={() => setMostraPassword(null)} />
      )}
      {impostaPassword && (
        <ImpostaPasswordCliente
          aziendaId={impostaPassword.azienda_id}
          email={impostaPassword.email}
          etichetta={`${impostaPassword.nome} ${impostaPassword.cognome} · ${impostaPassword.email}`}
          onChiudi={() => setImpostaPassword(null)}
          onSalvato={() => { setImpostaPassword(null); carica(); }}
        />
      )}
      {credenziali && (
        <PasswordTemporanea email={credenziali.email} password={credenziali.password} onChiudi={() => setCredenziali(null)} />
      )}
    </Layout>
  );
}

function livelloMostrato(u, sezione) {
  if (u.ruolo === 'cliente') return sezione.clienti ? <span style={styles.livello}>Propria azienda</span> : <span style={styles.nessuno}>—</span>;
  if (u.admin) return <span style={styles.livello}>Scrittura</span>;
  const livello = u.permessi?.[sezione.id];
  return livello ? <span style={styles.livello}>{ETICHETTE_LIVELLO[livello]}</span> : <span style={styles.nessuno}>—</span>;
}

const formDaUtente = (utente, sezioni) => ({
  nome: utente?.nome || '',
  cognome: utente?.cognome || '',
  email: utente?.email || '',
  ruolo: utente?.ruolo || 'studio',
  azienda_id: utente?.azienda_id || '',
  admin: Boolean(utente?.admin),
  version: utente?.version,
  ruolo_tariffario: utente?.ruolo_tariffario || '',
  permessi: Object.fromEntries(sezioni.map(s => [s.id, utente?.permessi?.[s.id] || ''])),
});

function FormUtente({ utente, sezioni, aziende, sonoAdmin, puoAccount, onChiudi, onSalvato }) {
  const [form, setForm] = useState(formDaUtente(utente, sezioni));
  const [passwordScelta, setPasswordScelta] = useState('');   // solo per i nuovi utenti azienda: vuota = generata dal server
  const [errore, setErrore] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [conflitto, setConflitto] = useState(null);

  const aggiorna = (campo, valore) => setForm(f => ({ ...f, [campo]: valore }));
  const aggiornaPermesso = (sezione, livello) => setForm(f => ({ ...f, permessi: { ...f.permessi, [sezione]: livello } }));

  const salva = async (version) => {
    setErrore('');
    setSalvataggio(true);
    const permessi = Object.fromEntries(Object.entries(form.permessi).map(([s, l]) => [s, l || null]));
    const dati = {
      nome: form.nome, cognome: form.cognome, email: form.email, ruolo: form.ruolo,
      azienda_id: form.ruolo === 'cliente' ? Number(form.azienda_id) || null : null,
      admin: form.ruolo === 'studio' && form.admin,
      permessi: form.ruolo === 'studio' ? permessi : undefined,
      ruolo_tariffario: form.ruolo === 'studio' ? form.ruolo_tariffario : '',
      version,
    };
    if (!utente && form.ruolo === 'cliente' && passwordScelta) {
      const e2 = validaPassword(passwordScelta, form.email, 'cliente');
      if (e2) { setErrore(e2); setSalvataggio(false); return; }
      dati.password = passwordScelta;
    }
    try {
      if (utente) {
        await api.put(`/utenti/${utente.id}`, dati);
        setConflitto(null);
        onSalvato();
      } else {
        const res = await api.post('/utenti', dati);
        onSalvato(res.data);
      }
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    } finally {
      setSalvataggio(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); salva(form.version); };

  return (
    <div style={styles.overlay}>
      {conflitto && (
        <ConflittoVersione
          conflitto={conflitto}
          inCorso={salvataggio}
          onRicarica={() => { setForm(formDaUtente(conflitto.attuale, sezioni)); setConflitto(null); }}
          onSovrascrivi={() => salva(conflitto.attuale.version)}
        />
      )}
      <div style={styles.modal}>
        <h3 style={{ marginTop: 0 }}>{utente ? `Modifica ${utente.nome} ${utente.cognome}` : 'Nuovo utente'}</h3>
        <form onSubmit={handleSubmit}>
          {errore && <div style={styles.errore}>{errore}</div>}
          <div style={styles.row}>
            <div style={styles.campo}>
              <label>Nome</label>
              <input type="text" value={form.nome} onChange={e => aggiorna('nome', e.target.value)} required style={styles.input} />
            </div>
            <div style={styles.campo}>
              <label>Cognome</label>
              <input type="text" value={form.cognome} onChange={e => aggiorna('cognome', e.target.value)} required style={styles.input} />
            </div>
          </div>
          <div style={styles.campo}>
            <label>Email di accesso</label>
            <input type="email" value={form.email} onChange={e => aggiorna('email', e.target.value)} required style={styles.input} />
          </div>
          <div style={styles.row}>
            <div style={styles.campo}>
              <label>Ruolo</label>
              <select value={form.ruolo} onChange={e => aggiorna('ruolo', e.target.value)} style={styles.input}>
                <option value="studio">Utente studio</option>
                {(puoAccount || form.ruolo === 'cliente') && <option value="cliente">Utente azienda</option>}
              </select>
            </div>
            {form.ruolo === 'cliente' && (
              <div style={styles.campo}>
                <label>Azienda</label>
                <select value={form.azienda_id} onChange={e => aggiorna('azienda_id', e.target.value)} required style={styles.input}>
                  <option value="">Scegli...</option>
                  {aziende.map(a => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
                </select>
              </div>
            )}
          </div>

          {form.ruolo === 'studio' && (
            <>
              <div style={styles.campo}>
                <label>Ruolo tariffario (per le tariffe orarie di Parcellazione)</label>
                <select value={form.ruolo_tariffario} onChange={e => aggiorna('ruolo_tariffario', e.target.value)} style={styles.input}>
                  <option value="">Nessuno</option>
                  <option value="operatrice">Operatrice</option>
                  <option value="professionista">Professionista</option>
                </select>
              </div>
              <label style={styles.checkbox}>
                <input type="checkbox" checked={form.admin} disabled={!sonoAdmin} onChange={e => aggiorna('admin', e.target.checked)} />
                Amministratore (accesso completo a tutte le sezioni)
              </label>
              {!form.admin && (
                <table style={{ ...styles.table, marginTop: '1rem', boxShadow: 'none', border: '1px solid #eee' }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Sezione</th>
                      {Object.entries(ETICHETTE_LIVELLO).map(([valore, etichetta]) => <th key={valore} style={{ ...styles.th, textAlign: 'center' }}>{etichetta}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {sezioni.map(s => (
                      <tr key={s.id} style={styles.tr}>
                        <td style={styles.td}>{s.nome}</td>
                        {Object.keys(ETICHETTE_LIVELLO).map(valore => (
                          <td key={valore} style={{ ...styles.td, textAlign: 'center' }}>
                            <input
                              type="radio"
                              name={`permesso-${s.id}`}
                              aria-label={`${s.nome}: ${ETICHETTE_LIVELLO[valore]}`}
                              checked={(form.permessi[s.id] || '') === valore}
                              onChange={() => aggiornaPermesso(s.id, valore)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          {form.ruolo === 'cliente' && (
            <p style={{ color: '#888', fontSize: '0.8rem' }}>Gli utenti azienda vedono solo gli intermittenti della propria azienda.</p>
          )}
          {!utente && form.ruolo === 'cliente' && (
            <div style={styles.campo}>
              <label>Scelta manuale dell'operatore (facoltativa)</label>
              <input type="text" aria-label="Password iniziale" value={passwordScelta} onChange={e => setPasswordScelta(e.target.value)} autoComplete="off" placeholder="Vuota: la genera il programma" style={styles.input} />
              <span style={{ color: '#888', fontSize: '0.78rem' }}>{REGOLA_PASSWORD_CLIENTE}</span>
            </div>
          )}
          {!utente && (
            <p style={{ color: '#888', fontSize: '0.8rem' }}>{form.ruolo === 'cliente' && passwordScelta ? 'La password scelta vale come temporanea: il cliente la cambia al primo accesso.' : 'La password temporanea viene generata al salvataggio e mostrata una sola volta.'}</p>
          )}

          <div style={styles.modalActions}>
            <button type="button" onClick={onChiudi} style={styles.btnSecondary}>Annulla</button>
            <button type="submit" disabled={salvataggio} style={styles.btn}>{salvataggio ? 'Salvataggio...' : 'Salva'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' },
  filtri: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' },
  inputCerca: { flex: '0 1 280px', minWidth: '180px', padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' },
  select: { padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', background: 'white' },
  // Selettore dell'elenco (studio / azienda / tutti): largo e nei colori del brand, per vedere subito quale elenco si sta guardando
  mostraDisattivati: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.95rem', color: '#232323', cursor: 'pointer' },
  selectElenco: { minWidth: '240px', padding: '0.6rem 0.8rem', border: '2px solid #D1D179', borderRadius: '6px', fontSize: '0.95rem', fontWeight: 600, color: '#232323', background: '#F7F7EA', cursor: 'pointer' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '0.8rem', background: '#f8f9fa', borderBottom: '2px solid #eee', fontSize: '0.85rem', color: '#555' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '0.7rem 0.8rem', fontSize: '0.9rem' },
  livello: { fontSize: '0.8rem', background: '#F7F7EA', padding: '0.15rem 0.5rem', borderRadius: '4px' },
  nessuno: { color: '#bbb' },
  badgeAdmin: { marginLeft: '0.4rem', fontSize: '0.7rem', background: '#D3D678', color: '#232323', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 600 },
  btn: { background: '#232323', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' },
  btnSecondary: { background: 'white', color: '#333', border: '1px solid #ddd', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' },
  btnSmall: { background: '#eef', color: '#232323', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer', marginRight: '0.3rem', fontSize: '0.8rem' },
  btnSmallDanger: { background: '#fee', color: '#c00', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { background: 'white', padding: '2rem', borderRadius: '12px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto', margin: '0 1rem' },
  campo: { marginBottom: '1rem', flex: 1 },
  input: { width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box', marginTop: '0.3rem' },
  row: { display: 'flex', gap: '1rem' },
  checkbox: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' },
  errore: { background: '#fee', color: '#c00', padding: '0.7rem', borderRadius: '6px', marginBottom: '1rem' },
};
